import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, ShieldCheck, Wrench, TrendingUp, Sparkles, Check, AlertTriangle, Loader2,
  MessageCircle, Send, Link2, Car, Search, ArrowLeft, Image as ImageIcon, Upload,
  Download, Share2, Truck, ChevronDown,
} from "lucide-react";
import WatchDogPanel from "../components/WatchDogPanel";
import { SofiAvatar } from "../components/SofiAvatar";
import OrderModal from "../components/OrderModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUELS = [
  { v: "petrol", l: "Бензин" },
  { v: "diesel", l: "Дизель" },
  { v: "hybrid", l: "Гібрид" },
  { v: "electric", l: "Електро" },
];

const METRICS = [
  { key: "value", label: "Вигода", icon: Wallet },
  { key: "trust", label: "Довіра", icon: ShieldCheck },
  { key: "condition", label: "Стан", icon: Wrench },
  { key: "market", label: "Ринок", icon: TrendingUp },
];

const fmt = (n) => (n == null ? "—" : new Intl.NumberFormat("uk-UA").format(Math.round(n)));

function getSofiSession() {
  let s = localStorage.getItem("sofi_session");
  if (!s) { s = `sofi-${Math.random().toString(36).slice(2, 14)}`; localStorage.setItem("sofi_session", s); }
  return s;
}

function scoreColor(v) {
  if (v >= 80) return "#22c55e";
  if (v >= 65) return "#a3e635";
  if (v >= 50) return "#f59e0b";
  return "#C73D3A";
}

function Gauge({ value }) {
  const r = 78, c = 2 * Math.PI * r, off = c * (1 - value / 100);
  return (
    <div className="relative flex h-[190px] w-[190px] items-center justify-center">
      <svg width="190" height="190" className="-rotate-90">
        <circle cx="95" cy="95" r={r} stroke="#1f1f1f" strokeWidth="14" fill="none" />
        <motion.circle
          cx="95" cy="95" r={r} stroke={scoreColor(value)} strokeWidth="14" fill="none"
          strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: off }}
          transition={{ duration: 1.1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-heading text-5xl font-bold text-white" data-testid="sofi-score-value">{value}</span>
        <span className="text-xs tracking-widest text-white/50">SOFI SCORE</span>
      </div>
    </div>
  );
}

function MetricBar({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3" data-testid={`metric-${label}`}>
      <Icon size={20} className="shrink-0 text-[#C73D3A]" />
      <span className="w-20 shrink-0 text-sm text-white/70">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
        <motion.div className="h-full rounded-full"
          style={{ backgroundColor: scoreColor(value) }}
          initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 0.9 }} />
      </div>
      <span className="w-8 shrink-0 text-right text-sm font-semibold text-white">{value}</span>
    </div>
  );
}

export default function SofiDemo() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "url" ? "url" : "find");
  const [sessionId] = useState(getSofiSession);

  const [form, setForm] = useState({
    make: "Ford", model: "Mondeo", year: 2019, mileage_km: 145000,
    fuel: "diesel", engine_cc: 1968, price: 12000,
  });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  // URL mode
  const [url, setUrl] = useState("");
  const [urlBusy, setUrlBusy] = useState(false);
  const [urlMsg, setUrlMsg] = useState(null);
  const [sourceUrl, setSourceUrl] = useState(null);
  const [source, setSource] = useState(null);
  const [shotBusy, setShotBusy] = useState(false);
  const shotInputRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const [photoVision, setPhotoVision] = useState(null);
  const photoInputRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [showOrder, setShowOrder] = useState(false);
  const [orderCard, setOrderCard] = useState(null);

  // find mode
  const [cards, setCards] = useState(null);
  const [findNote, setFindNote] = useState(null);
  const [findBusy, setFindBusy] = useState(false);
  const [findForm, setFindForm] = useState({
    make: "", model: "", fuel: "", price_max_eur: 18000,
    year_min: 2018, year_max: 2022, mileage_max: 180000,
  });
  const updF = (k, v) => setFindForm((f) => ({ ...f, [k]: v }));

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function runFind() {
    if (findBusy) return;
    setFindBusy(true); setError(null); setReport(null); setOrderCard(null);
    try {
      const c = findForm;
      const criteria = {
        make: c.make || null, model: c.model || null, fuel: c.fuel || null,
        price_max_eur: c.price_max_eur ? Number(c.price_max_eur) : null,
        year_min: c.year_min ? Number(c.year_min) : null,
        year_max: c.year_max ? Number(c.year_max) : null,
        mileage_max: c.mileage_max ? Number(c.mileage_max) : null, limit: 24,
      };
      const r = await fetch(`${API}/sofi/find`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ criteria }),
      });
      const d = await r.json();
      setCards(Array.isArray(d?.cards) ? d.cards : []);
      setFindNote(d?.note || null);
    } catch { setError("Не вдалося виконати пошук. Спробуйте ще раз."); }
    setFindBusy(false);
  }

  async function analyzeCard(card) {
    setForm({
      make: card.make, model: card.model, year: card.year, mileage_km: card.mileage_km,
      fuel: card.fuel, engine_cc: card.engine_cc, price: card.listing_price,
    });
    setSourceUrl(card.url); setSource(card.source);
    setLoading(true); setError(null); setReport(null);
    try {
      const res = await fetch(`${API}/sofi/report-from-manual`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          make: card.make, model: card.model, year: Number(card.year),
          mileage_km: Number(card.mileage_km), fuel: card.fuel,
          engine_cc: Number(card.engine_cc), price: Number(card.listing_price),
          currency: "EUR", source_url: card.url,
        }),
      });
      setReport(await res.json());
    } catch { setError("Не вдалося отримати аналіз"); }
    setLoading(false);
  }

  function orderCardNow(card) {
    setOrderCard(card);
    setForm({
      make: card.make, model: card.model, year: card.year, mileage_km: card.mileage_km,
      fuel: card.fuel, engine_cc: card.engine_cc, price: card.listing_price,
    });
    setSourceUrl(card.url); setSource(card.source); setShowOrder(true);
  }

  async function analyzeUrl() {
    const link = url.trim();
    if (!link || urlBusy) return;
    setUrlBusy(true); setUrlMsg(null); setError(null); setReport(null);
    try {
      const res = await fetch(`${API}/sofi/analyze-url`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: link }),
      });
      const d = await res.json();
      setSourceUrl(link);
      if (res.status === 429 || res.status === 503) {
        setMode("url");
        setUrlMsg(d?.detail || "Служба зараз завантажена. Спробуйте за кілька хвилин.");
        setUrlBusy(false);
        return;
      }
      if (d && d.score) {
        setSource(d.listing_market ? "auto" : null);
        setReport(d);
      } else {
        // needs manual — prefill whatever the provider could parse (never invented)
        setSource(d.source && d.source !== "unknown" ? d.source : null);
        if (d.prefill) {
          setForm((f) => ({
            make: d.prefill.make || f.make, model: d.prefill.model || f.model,
            year: d.prefill.year || f.year, mileage_km: d.prefill.mileage_km || f.mileage_km,
            fuel: d.prefill.fuel || f.fuel, engine_cc: d.prefill.engine_cc || f.engine_cc,
            price: d.prefill.price || f.price,
          }));
        }
        setMode("url");
        setUrlMsg(d.message_uk || "Джерело не вдалося зчитати автоматично. Доповніть дані нижче — і Софі підготує звіт.");
      }
    } catch (e) {
      setError("Не вдалося обробити посилання. Спробуйте ще раз або введіть дані вручну.");
    }
    setUrlBusy(false);
  }

  async function analyzeScreenshot(fileList) {
    const f = fileList && fileList[0];
    if (!f || shotBusy) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      setUrlMsg("Підтримуються лише JPEG, PNG або WEBP.");
      return;
    }
    setShotBusy(true); setError(null); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (sourceUrl || url.trim()) fd.append("source_url", sourceUrl || url.trim());
      const res = await fetch(`${API}/sofi/analyze-screenshot`, { method: "POST", body: fd });
      const d = await res.json();
      if (res.status === 429 || res.status === 503 || res.status >= 400) {
        setUrlMsg(d?.detail || "Не вдалося опрацювати зображення. Спробуйте ще раз.");
      } else if (d && d.score) {
        setSource("screenshot");
        setReport(d);
        setUrlMsg(null);
      } else {
        if (d.prefill) {
          setForm((prev) => ({
            make: d.prefill.make || prev.make, model: d.prefill.model || prev.model,
            year: d.prefill.year || prev.year, mileage_km: d.prefill.mileage_km || prev.mileage_km,
            fuel: d.prefill.fuel || prev.fuel, engine_cc: d.prefill.engine_cc || prev.engine_cc,
            price: d.prefill.price || prev.price,
          }));
        }
        setUrlMsg(d.message_uk || "Зі скріншота вдалося зчитати не все — доповніть поля нижче.");
      }
    } catch {
      setUrlMsg("Не вдалося опрацювати зображення. Спробуйте ще раз.");
    }
    setShotBusy(false);
    if (shotInputRef.current) shotInputRef.current.value = "";
  }

  async function analyzePhotos(fileList) {
    const files = Array.from(fileList || []).slice(0, 20);
    if (!files.length || photoBusy) return;
    setPhotoBusy(true); setError(null); setReport(null); setPhotoVision(null);
    setPhotoCount(files.length);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (sourceUrl || url.trim()) fd.append("source_url", sourceUrl || url.trim());
      if (form.year) fd.append("year", Number(form.year));
      if (form.mileage_km) fd.append("declared_mileage_km", Number(form.mileage_km));
      const res = await fetch(`${API}/sofi/analyze-photos`, { method: "POST", body: fd });
      const d = await res.json();
      if (res.status >= 400) {
        setUrlMsg(d?.detail || "Не вдалося опрацювати фото. Спробуйте ще раз.");
      } else if (d && d.score) {
        setSource("photos"); setReport(d); setUrlMsg(null);
      } else {
        if (d.prefill) {
          setForm((prev) => ({
            make: d.prefill.make || prev.make, model: d.prefill.model || prev.model,
            year: d.prefill.year || prev.year, mileage_km: d.prefill.mileage_km || prev.mileage_km,
            fuel: d.prefill.fuel || prev.fuel, engine_cc: d.prefill.engine_cc || prev.engine_cc,
            price: d.prefill.price || prev.price,
          }));
        }
        setPhotoVision(d.vision || null);
        setUrlMsg(d.message_uk || "З фото вдалося зчитати не все — доповніть поля нижче.");
      }
    } catch {
      setUrlMsg("Не вдалося опрацювати фото. Спробуйте ще раз.");
    }
    setPhotoBusy(false);
    if (photoInputRef.current) photoInputRef.current.value = "";
  }

  function pdfFileName() {
    const v = report?.vehicle || {};
    const n = [v.make, v.model, v.year].filter(Boolean).join("-") || "avto";
    return `ADT-Sofi-${n}.pdf`.replace(/\s+/g, "-");
  }

  async function fetchPdfBlob() {
    const res = await fetch(`${API}/sofi/report-pdf`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(report),
    });
    if (!res.ok) throw new Error("pdf");
    return await res.blob();
  }

  async function downloadPdf() {
    if (!report || pdfBusy) return;
    setPdfBusy(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = pdfFileName();
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      setError("Не вдалося створити PDF. Спробуйте ще раз.");
    }
    setPdfBusy(false);
  }

  async function sharePdf() {
    if (!report || pdfBusy) return;
    setPdfBusy(true);
    try {
      const blob = await fetchPdfBlob();
      const file = new File([blob], pdfFileName(), { type: "application/pdf" });
      const title = `Звіт ADT · Софі — ${(report.vehicle?.make || "")} ${(report.vehicle?.model || "")}`.trim();
      const text = `Орієнтовна ціна під ключ в Україні: ${fmt(report.final_price_uah)} ₴. Звіт від Софі (ADT).`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text });
      } else if (navigator.share) {
        await navigator.share({ title, text, url: "https://avtodlatebe.com.ua" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = pdfFileName();
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      }
    } catch (e) {
      if (e && e.name !== "AbortError") setError("Поділитися не вдалося. Спробуйте завантажити PDF.");
    }
    setPdfBusy(false);
  }

  async function analyze() {
    setLoading(true); setError(null); setReport(null);
    try {
      const res = await fetch(`${API}/sofi/report-from-manual`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form, source_url: sourceUrl || undefined,
          year: Number(form.year), mileage_km: Number(form.mileage_km),
          engine_cc: Number(form.engine_cc), price: Number(form.price), currency: "EUR",
        }),
      });
      if (!res.ok) throw new Error("Не вдалося отримати аналіз");
      setReport(await res.json());
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  const savingsPositive = report?.savings_pct != null && report.savings_pct > 0;
  const goodResult = report && (savingsPositive || report.score.overall >= 65);
  const heroMood = (loading || urlBusy) ? "thinking" : report ? (goodResult ? "happy" : "cautious") : "idle";
  const reportMood = goodResult ? "happy" : "cautious";
  const analyzing = loading || urlBusy || shotBusy || photoBusy;

  const orderContext = {
    sessionId, mode, sourceUrl, source,
    make: form.make, model: form.model, year: Number(form.year),
    listing_price: Number(form.price), currency: "EUR",
    final_price_uah: report?.final_price_uah ?? orderCard?.final_price_uah,
    sofi_score: report?.score?.overall ?? orderCard?.sofi_score,
    recommendation_uk: report?.score?.grade_uk ?? orderCard?.recommendation_uk,
    report_id: report?.report_id, report_snapshot: report || orderCard || {},
  };

  return (
    <div className="min-h-screen bg-[#070707] font-body text-white">
      <div className="pointer-events-none fixed inset-0 opacity-70"
        style={{ background: "radial-gradient(60% 40% at 80% 0%, rgba(177,42,42,0.25), transparent 60%)" }} />

      <div className="relative mx-auto max-w-5xl px-5 py-12">
        {/* hero */}
        <div className="flex items-center gap-5">
          <SofiAvatar size={96} state={heroMood} className="shrink-0" testid="sofi-avatar" />
          <div>
            <div className="flex items-center gap-2 text-[#C73D3A]">
              <Sparkles size={18} /><span className="text-xs font-semibold tracking-[0.3em]">ADT · АВТО ДЛЯ ТЕБЕ</span>
            </div>
            <h1 className="font-heading text-4xl font-bold sm:text-5xl">Привіт, я Софі 👋</h1>
            <p className="mt-1 text-white/60">Не просто відповідаю — <span className="text-white/90">раджу</span>. Знайду авто, перевірю його та порахую кінцеву ціну на українських номерах.</p>
          </div>
        </div>

        {/* mode toggle */}
        <div className="mt-8 inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1" data-testid="mode-toggle">
          <button data-testid="mode-find" onClick={() => setMode("find")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${mode === "find" ? "bg-[#C73D3A] text-white" : "text-white/60 hover:text-white"}`}>
            <Car size={16} /> Підібрати за параметрами
          </button>
          <button data-testid="mode-url" onClick={() => setMode("url")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${mode === "url" ? "bg-[#C73D3A] text-white" : "text-white/60 hover:text-white"}`}>
            <Link2 size={16} /> Вставити посилання
          </button>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[380px_1fr]">
          {/* left: input */}
          <div className="space-y-4">
            {mode === "find" && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur" data-testid="find-panel">
                <h2 className="mb-1 font-heading text-lg font-semibold">Знайти авто</h2>
                <p className="mb-4 text-xs text-white/50">Софі підбере найкращі варіанти й порахує кінцеву ціну в Україні.</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Марка"><input data-testid="find-make" className="inp" placeholder="Будь-яка" value={findForm.make} onChange={(e) => updF("make", e.target.value)} /></Field>
                  <Field label="Модель"><input data-testid="find-model" className="inp" placeholder="Будь-яка" value={findForm.model} onChange={(e) => updF("model", e.target.value)} /></Field>
                  <Field label="Пальне">
                    <select data-testid="find-fuel" className="inp" value={findForm.fuel} onChange={(e) => updF("fuel", e.target.value)}>
                      <option value="">Будь-яке</option>
                      {FUELS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                    </select>
                  </Field>
                  <Field label="Ціна до, €"><input data-testid="find-price" type="number" className="inp" value={findForm.price_max_eur} onChange={(e) => updF("price_max_eur", e.target.value)} /></Field>
                  <Field label="Рік від"><input data-testid="find-year-min" type="number" className="inp" value={findForm.year_min} onChange={(e) => updF("year_min", e.target.value)} /></Field>
                  <Field label="Рік до"><input data-testid="find-year-max" type="number" className="inp" value={findForm.year_max} onChange={(e) => updF("year_max", e.target.value)} /></Field>
                  <div className="col-span-2">
                    <Field label="Пробіг до, км"><input data-testid="find-mileage" type="number" className="inp" value={findForm.mileage_max} onChange={(e) => updF("mileage_max", e.target.value)} /></Field>
                  </div>
                </div>
                <button data-testid="find-btn" onClick={runFind} disabled={findBusy}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#C73D3A] px-5 py-3.5 font-heading font-semibold tracking-wide transition-colors hover:bg-[#d84a47] disabled:opacity-60">
                  {findBusy ? <><Loader2 size={18} className="animate-spin" /> Софі шукає…</> : <><Search size={18} /> Найди mi auto</>}
                </button>
                {error && <p className="mt-3 text-sm text-[#C73D3A]" data-testid="error-msg">{error}</p>}
              </div>
            )}

            {mode === "url" && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur" data-testid="url-panel">
                <h2 className="mb-2 font-heading text-lg font-semibold">Посилання на авто</h2>
                <p className="mb-4 text-xs text-white/50">Mobile.de, AutoScout24, AutoRia, OLX, Bazoš, Sauto, TipCars…</p>
                <input data-testid="url-input" className="inp" placeholder="https://…"
                  value={url} onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && analyzeUrl()} />
                <button data-testid="url-analyze-btn" onClick={analyzeUrl} disabled={urlBusy}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#C73D3A] px-5 py-3.5 font-heading font-semibold tracking-wide transition-colors hover:bg-[#d84a47] disabled:opacity-60">
                  {urlBusy ? <><Loader2 size={18} className="animate-spin" /> Софі аналізує…</> : <>Аналізувати посилання</>}
                </button>
              </div>
            )}

            {mode === "url" && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur" data-testid="photos-panel">
                <div className="mb-1 flex items-center gap-2">
                  <ImageIcon size={18} className="text-[#C73D3A]" />
                  <h2 className="font-heading text-lg font-semibold">Глибокий фото-аналіз</h2>
                </div>
                <p className="mb-4 text-xs leading-relaxed text-white/55">
                  Завантажте до 20 фото авто (екстер'єр, салон, двигун, VIN, сервісна книжка, скріншот
                  оголошення). Софі оцінить стан як досвідчений експерт і чесно попередить про ризики.
                </p>
                <input ref={photoInputRef} type="file" multiple accept="image/png,image/jpeg,image/webp"
                  className="hidden" data-testid="photos-input"
                  onChange={(e) => analyzePhotos(e.target.files)} />
                <button data-testid="photos-btn" disabled={photoBusy}
                  onClick={() => photoInputRef.current && photoInputRef.current.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#C73D3A] px-5 py-3.5 font-heading font-semibold tracking-wide transition-colors hover:bg-[#d84a47] disabled:opacity-60">
                  {photoBusy ? <><Loader2 size={18} className="animate-spin" /> Софі вивчає {photoCount} фото…</>
                            : <><Upload size={18} /> Обрати фото авто</>}
                </button>
                {photoVision && <VisionBlock v={photoVision} />}
              </div>
            )}

            {mode === "url" && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
              <h2 className="mb-4 font-heading text-lg font-semibold">Дані автомобіля</h2>
              {urlMsg && (
                <div className="mb-4 rounded-lg border border-[#f59e0b]/30 bg-[#f59e0b]/10 p-3 text-xs text-[#f0c07a]" data-testid="url-needs-manual">
                  {urlMsg}
                </div>
              )}

              {urlMsg && (
                <div className="mb-5 rounded-xl border border-[#C73D3A]/30 bg-[#C73D3A]/[0.06] p-4" data-testid="screenshot-upload">
                  <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-[#e08a85]">
                    <ImageIcon size={16} /> Завантажте скріншот оголошення
                  </div>
                  <p className="mb-3 text-xs leading-relaxed text-white/55">
                    Деякі портали (напр. Mobile.de) блокують автозчитування. Зробіть скріншот сторінки з
                    ціною й характеристиками — Софі зчитає дані сама.
                  </p>
                  <input ref={shotInputRef} type="file" accept="image/png,image/jpeg,image/webp"
                    className="hidden" data-testid="screenshot-input"
                    onChange={(e) => analyzeScreenshot(e.target.files)} />
                  <button data-testid="screenshot-btn" disabled={shotBusy}
                    onClick={() => shotInputRef.current && shotInputRef.current.click()}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#C73D3A]/50 bg-[#C73D3A]/10 px-5 py-3 font-heading text-sm font-semibold tracking-wide transition-colors hover:bg-[#C73D3A]/20 disabled:opacity-60">
                    {shotBusy ? <><Loader2 size={16} className="animate-spin" /> Софі читає скріншот…</>
                              : <><Upload size={16} /> Обрати скріншот</>}
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Марка"><input data-testid="in-make" className="inp" value={form.make} onChange={(e) => upd("make", e.target.value)} /></Field>
                <Field label="Модель"><input data-testid="in-model" className="inp" value={form.model} onChange={(e) => upd("model", e.target.value)} /></Field>
                <Field label="Рік"><input data-testid="in-year" type="number" className="inp" value={form.year} onChange={(e) => upd("year", e.target.value)} /></Field>
                <Field label="Пробіг, км"><input data-testid="in-mileage" type="number" className="inp" value={form.mileage_km} onChange={(e) => upd("mileage_km", e.target.value)} /></Field>
                <Field label="Пальне">
                  <select data-testid="in-fuel" className="inp" value={form.fuel} onChange={(e) => upd("fuel", e.target.value)}>
                    {FUELS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                  </select>
                </Field>
                <Field label="Об'єм, см³"><input data-testid="in-engine" type="number" className="inp" value={form.engine_cc} onChange={(e) => upd("engine_cc", e.target.value)} /></Field>
                <div className="col-span-2">
                  <Field label="Ціна в Європі, €"><input data-testid="in-price" type="number" className="inp" value={form.price} onChange={(e) => upd("price", e.target.value)} /></Field>
                </div>
              </div>
              <button data-testid="analyze-btn" onClick={analyze} disabled={loading}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#C73D3A] px-5 py-3.5 font-heading font-semibold tracking-wide transition-colors hover:bg-[#d84a47] disabled:opacity-60">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Софі аналізує…</> : <>Спитати Софі</>}
              </button>
              {error && <p className="mt-3 text-sm text-[#C73D3A]" data-testid="error-msg">{error}</p>}
            </div>
            )}
          </div>

          {/* result */}
          <div className="min-h-[300px]">
            <AnimatePresence mode="wait">
              {analyzing && (
                <motion.div key="busy" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <SofiProgress deep={photoBusy || shotBusy} />
                </motion.div>
              )}
              {!analyzing && mode === "find" && cards && cards.length > 0 && !report && !loading && (
                <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="space-y-4">
                  {findNote && (
                    <div className="rounded-xl border border-[#f59e0b]/30 bg-[#f59e0b]/10 p-3 text-sm text-[#f0c07a]" data-testid="find-note">
                      {findNote}
                    </div>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2" data-testid="find-results">
                    {cards.map((c, i) => (
                      <CarCard key={i} c={c} onAnalyze={analyzeCard} onOrder={orderCardNow} />
                    ))}
                  </div>
                </motion.div>
              )}
              {!analyzing && mode === "find" && cards && cards.length === 0 && !loading && (
                <motion.div key="noresults" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="flex h-full items-center justify-center rounded-2xl border border-dashed border-white/10 p-10 text-center text-white/40" data-testid="find-empty">
                  Нічого не знайшли за цими критеріями. Спробуйте розширити пошук.
                </motion.div>
              )}
              {!analyzing && !report && !loading && !(mode === "find" && cards) && (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex h-full items-center justify-center rounded-2xl border border-dashed border-white/10 p-10 text-center text-white/40">
                  {mode === "url"
                    ? "Вставте посилання на авто — Софі перевірить його та порахує кінцеву ціну в Україні."
                    : "Задайте критерії та натисніть «Найди mi auto» — я підберу найкращі варіанти."}
                </motion.div>
              )}
              {report && (
                <motion.div key="report" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  className="space-y-5" data-testid="sofi-report">
                  {mode === "find" && cards && cards.length > 0 && (
                    <button data-testid="back-to-results" onClick={() => setReport(null)}
                      className="flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-white">
                      <ArrowLeft size={16} /> Назад до результатів
                    </button>
                  )}
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                    <div className="flex flex-col items-center gap-6 sm:flex-row">
                      <Gauge value={report.score.overall} />
                      <div className="flex-1 space-y-3.5">
                        <div className="mb-1 font-heading text-xl font-bold" data-testid="sofi-grade">{report.score.grade_uk}</div>
                        {METRICS.map((m) => <MetricBar key={m.key} icon={m.icon} label={m.label} value={report.score.metrics[m.key]} />)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Stat label="Ціна під ключ (UA)" value={`${fmt(report.final_price_uah)} ₴`} testid="stat-final" />
                    <Stat label="Ринок України" value={`${fmt(report.market_value_uah)} ₴`} testid="stat-market" />
                    <Stat label="Різниця"
                      value={`${savingsPositive ? "−" : "+"}${fmt(Math.abs(report.savings_uah || 0))} ₴`}
                      color={savingsPositive ? "#22c55e" : "#C73D3A"} testid="stat-savings" />
                  </div>

                  {report.price_note_uk && (
                    <p className="text-xs leading-relaxed text-white/40" data-testid="price-note">{report.price_note_uk}</p>
                  )}

                  {report.vat && <VatBlock v={report.vat} fmt={fmt} />}

                  {report.import_vs_local && (
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="import-vs-local">
                      <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-[#e08a85]">
                        <Car size={16} /> {report.import_vs_local.title_uk}
                      </div>
                      <p className="text-sm leading-relaxed text-white/70">{report.import_vs_local.detail_uk}</p>
                    </div>
                  )}

                  {/* ORDER CTA — primary business goal: convert to a personal consultation */}
                  <button data-testid="order-cta" onClick={() => setShowOrder(true)}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#C73D3A] px-6 py-4 font-heading text-base font-bold tracking-wide text-white transition-colors hover:bg-[#d84a47]">
                    <Car size={20} /> 🚗 Безкоштовно проконсультуватися щодо довозу цього авто
                  </button>

                  <div className="grid grid-cols-2 gap-3">
                    <button data-testid="download-pdf-btn" disabled={pdfBusy} onClick={downloadPdf}
                      className="flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10 disabled:opacity-60">
                      {pdfBusy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Завантажити PDF
                    </button>
                    <button data-testid="share-pdf-btn" disabled={pdfBusy} onClick={sharePdf}
                      className="flex items-center justify-center gap-2 rounded-xl border border-[#C73D3A]/40 bg-[#C73D3A]/10 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#C73D3A]/20 disabled:opacity-60">
                      <Share2 size={16} /> Поділитися
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs">
                    <Chip>Впевненість ринку: {report.market_confidence}%</Chip>
                    <Chip>Якість даних: {report.data_quality}%</Chip>
                    <Chip>{report.listing_market === "ua" ? "Джерело: Україна" : "Джерело: Європа"}</Chip>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <List title="Переваги" items={report.advantages_uk} icon={Check} color="#22c55e" testid="advantages" />
                    <List title="Застереження" items={report.warnings_uk} icon={AlertTriangle} color="#f59e0b" testid="warnings" />
                  </div>

                  <div className="flex gap-3 rounded-2xl border border-[#C73D3A]/30 bg-[#C73D3A]/[0.06] p-5" data-testid="sofi-narrative">
                    <SofiAvatar size={44} state={reportMood} ringWidth={1} pop className="shrink-0" testid="sofi-narrative-avatar" />
                    <div>
                      <div className="mb-1 text-sm font-semibold text-[#e08a85]">Висновок Софі</div>
                      <p className="whitespace-pre-line text-[15px] leading-relaxed text-white/85">{report.narrative_uk}</p>
                    </div>
                  </div>

                  {report.vision && (report.vision.available || (report.vision.recommended_photos_uk || []).length > 0) && (
                    <VisionBlock v={report.vision} />
                  )}

                  <ImportTimeline />

                  <p className="text-xs text-white/40">{report.disclaimer_uk}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <ChatPanel vehicle={form} />
        <WatchDogPanel vehicle={form} />
      </div>

      <OrderModal open={showOrder} onClose={() => setShowOrder(false)} context={orderContext} />

      <style>{`.inp{width:100%;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.12);border-radius:10px;padding:9px 12px;color:#fff;font-size:14px;outline:none}.inp:focus{border-color:#C73D3A}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="mb-1 block text-xs text-white/50">{label}</span>{children}</label>
);
const Stat = ({ label, value, color, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
    <div className="text-xs text-white/50">{label}</div>
    <div className="mt-1 font-heading text-lg font-bold" style={{ color: color || "#fff" }}>{value}</div>
  </div>
);
const Chip = ({ children }) => (
  <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-white/70">{children}</span>
);
const List = ({ title, items, icon: Icon, color, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
    <div className="mb-2 text-sm font-semibold text-white/80">{title}</div>
    <ul className="space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-white/75">
          <Icon size={16} className="mt-0.5 shrink-0" style={{ color }} />{t}
        </li>
      ))}
    </ul>
  </div>
);

const VisionRow = ({ label, value }) =>
  value ? (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-semibold text-white/45">{label}</span>
      <span className="text-sm leading-relaxed text-white/80">{value}</span>
    </div>
  ) : null;

const VisionList = ({ label, items, color = "#f59e0b" }) =>
  items && items.length ? (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-white/45">{label}</span>
      <ul className="space-y-1">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/80">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />{t}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

const DEEP_STAGES = [
  "📄 Читаю дані з фотографій…",
  "🔍 Шукаю VIN і технічні дані…",
  "🚗 Аналізую стан кузова…",
  "🎨 Перевіряю можливі перефарбовані деталі…",
  "📊 Рахую кінцеву ціну на українських номерах…",
  "🤖 Софі готує рекомендації…",
];
const QUICK_STAGES = [
  "🔗 Відкриваю оголошення…",
  "📄 Зчитую характеристики авто…",
  "📊 Рахую кінцеву ціну на українських номерах…",
  "🤖 Софі готує висновок…",
];

const SofiProgress = ({ deep }) => {
  const stages = deep ? DEEP_STAGES : QUICK_STAGES;
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    const t = setInterval(() => setI((p) => Math.min(p + 1, stages.length - 1)),
      deep ? 6000 : 2500);
    return () => clearInterval(t);
  }, [deep]);
  return (
    <div className="flex h-full min-h-[360px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center" data-testid="sofi-progress">
      <SofiAvatar size={96} state="thinking" ringWidth={2} pop testid="progress-avatar" />
      <AnimatePresence mode="wait">
        <motion.p key={i} data-testid="sofi-progress-stage"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="mt-7 max-w-xs font-heading text-lg font-semibold text-white">
          {stages[i]}
        </motion.p>
      </AnimatePresence>
      <div className="mt-6 flex gap-1.5" data-testid="sofi-progress-dots">
        {stages.map((_, k) => (
          <span key={k}
            className={`h-1.5 rounded-full transition-all duration-500 ${k <= i ? "w-6 bg-[#C73D3A]" : "w-1.5 bg-white/20"}`} />
        ))}
      </div>
      <p className="mt-5 text-xs text-white/40">
        {deep ? "Глибокий аналіз може тривати до хвилини — Софі уважно вивчає кожне фото."
              : "Це займе кілька секунд…"}
      </p>
    </div>
  );
};

const TIMELINE = [
  { t: "Прийняття замовлення", d: "Опрацьовуємо вашу заявку та узгоджуємо деталі." },
  { t: "Виїзд до автомобіля", d: "Зазвичай протягом 1–2 днів після підтвердження замовлення." },
  { t: "Особистий огляд", d: "Технічний стан, діагностика, тест-драйв і перевірка документів." },
  { t: "Купівля авто", d: "Одразу після вашого схвалення авто викуповується." },
  { t: "Експорт з ЄС", d: "Оформлення експортних номерів і документів — зазвичай наступного робочого дня." },
  { t: "Доставка в Україну", d: "Організовуємо безпечне транспортування." },
  { t: "Митне оформлення", d: "Здебільшого протягом одного дня. За незгоди з оцінкою митниці можливе оскарження — процес може подовжитися на кілька днів." },
  { t: "Сертифікація авто", d: "Проходимо обов'язкову сертифікацію." },
  { t: "Реєстрація на українських номерах", d: "Зазвичай авто готове до передачі ~7 днів від купівлі, якщо немає надзвичайних обставин." },
  { t: "Повернення ПДВ", d: "За наявності права: готуємо всі документи та ведемо комунікацію з продавцем. Здебільшого ПДВ повертається ~30 днів після подання повного пакету." },
];
const TIMELINE_NOTE =
  "Терміни орієнтовні й можуть змінюватися (черги на кордоні, дії митниці, вимоги конкретного продавця). " +
  "ADT відкрито пояснює весь процес і тримає вас у курсі статусу довозу.";

const ImportTimeline = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="import-timeline">
      <button data-testid="timeline-toggle" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 p-5 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-white/85">
          <Truck size={16} className="text-[#C73D3A]" /> Як відбувається довіз · орієнтовні терміни
        </span>
        <ChevronDown size={18} className={`shrink-0 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="tl" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="px-5 pb-5">
              <ol className="relative space-y-4 border-l border-white/10 pl-5">
                {TIMELINE.map((s, i) => (
                  <li key={i} className="relative" data-testid={`timeline-step-${i + 1}`}>
                    <span className="absolute -left-[26px] flex h-5 w-5 items-center justify-center rounded-full bg-[#C73D3A] text-[10px] font-bold text-white">{i + 1}</span>
                    <div className="text-sm font-semibold text-white/90">{s.t}</div>
                    <div className="text-xs leading-relaxed text-white/55">{s.d}</div>
                  </li>
                ))}
              </ol>
              <p className="mt-4 text-xs italic leading-relaxed text-white/40">{TIMELINE_NOTE}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const VatBlock = ({ v, fmt }) => {
  if (!v.available) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-xs text-white/45" data-testid="vat-block">
        {v.note_uk}
      </div>
    );
  }
  const cur = v.currency === "EUR" ? "€" : v.currency === "CZK" ? "Kč" : v.currency;
  return (
    <div className="rounded-2xl border border-[#22c55e]/30 bg-[#22c55e]/[0.06] p-5" data-testid="vat-block">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#7ee0a0]">
        <Sparkles size={16} /> Можливе повернення ПДВ (експорт за межі ЄС)
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-white/[0.03] p-3" data-testid="vat-brutto">
          <div className="text-[10px] uppercase tracking-wide text-white/40">Ціна Brutto</div>
          <div className="text-base font-bold text-white">{fmt(v.brutto_price)} {cur}</div>
        </div>
        <div className="rounded-lg bg-white/[0.03] p-3" data-testid="vat-netto">
          <div className="text-[10px] uppercase tracking-wide text-white/40">Ціна Netto</div>
          <div className="text-base font-bold text-white">{fmt(v.netto_price)} {cur}</div>
        </div>
        <div className="rounded-lg bg-[#22c55e]/10 p-3" data-testid="vat-refund">
          <div className="text-[10px] uppercase tracking-wide text-white/40">Повернене ПДВ{v.vat_rate_pct ? ` (${v.vat_rate_pct}%)` : ""}</div>
          <div className="text-base font-bold text-[#7ee0a0]">{fmt(v.vat_refund)} {cur}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wide text-white/40">Під ключ — сплата Brutto</div>
          <div className="text-base font-bold text-white">{fmt(v.final_price_uah_brutto)} ₴</div>
        </div>
        <div className="rounded-lg border border-[#22c55e]/30 bg-[#22c55e]/10 p-3">
          <div className="text-[10px] uppercase tracking-wide text-white/40">Реальна ціна після повернення ПДВ</div>
          <div className="text-base font-bold text-[#7ee0a0]">{fmt(v.final_price_uah_netto)} ₴</div>
        </div>
      </div>
      <div className="mt-3 text-sm font-semibold text-[#7ee0a0]" data-testid="vat-savings">
        💚 Ви реально економите ≈ {fmt(v.savings_uah)} ₴
      </div>
      <p className="mt-3 text-xs leading-relaxed text-white/60">{v.info_uk}</p>
    </div>
  );
};

const VisionBlock = ({ v }) => (
  <div className="mt-4 space-y-4 rounded-2xl border border-[#C73D3A]/25 bg-[#C73D3A]/[0.05] p-5" data-testid="vision-block">
    <div className="flex items-center gap-2">
      <SofiAvatar size={36} state="thinking" ringWidth={1} className="shrink-0" testid="vision-avatar" />
      <div>
        <div className="text-sm font-semibold text-[#e08a85]">Фото-аналіз Софі</div>
        <div className="text-xs text-white/45">
          Проаналізовано фото: {v.photos_analyzed ?? 0} · впевненість {v.confidence ?? 0}%
        </div>
      </div>
    </div>
    <VisionRow label="Джерело" value={v.source_detected_uk} />
    <VisionRow label="Оцінка стану" value={v.wear_estimate_uk} />
    <VisionRow label="Пробіг vs вигляд" value={v.mileage_consistency_uk} />
    <VisionRow label="Ризик сточеного пробігу" value={v.odometer_rollback_risk_uk} />
    <VisionList label="Можливо перефарбовані деталі" items={v.painted_panels_uk} />
    <VisionList label="Ознаки ремонту" items={v.possible_repairs_uk} />
    <VisionList label="Невідповідності фото/опис" items={v.description_mismatches_uk} color="#C73D3A" />
    <VisionList label="Підозрілі деталі" items={v.suspicious_details_uk} color="#C73D3A" />
    <VisionList label="Перевірка узгодженості фото" items={v.cross_check_uk} color="#22c55e" />
    <VisionList label="Софі радить додати фото" items={v.recommended_photos_uk} color="#a3e635" />
    {v.note_uk && <p className="text-xs italic leading-relaxed text-white/50">{v.note_uk}</p>}
  </div>
);

const CarCard = ({ c, onAnalyze, onOrder }) => (
  <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="car-card">
    <div className="aspect-[16/10] overflow-hidden bg-white/5">
      {c.photo && <img src={c.photo} alt={`${c.make} ${c.model}`} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />}
    </div>
    <div className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="font-heading font-bold leading-tight">{c.make} {c.model}</div>
        <span className="shrink-0 rounded-full px-2 py-0.5 text-xs font-bold"
          style={{ background: `${scoreColor(c.sofi_score)}22`, color: scoreColor(c.sofi_score) }}>
          {c.sofi_score}
        </span>
      </div>
      <div className="mt-1 text-xs text-white/45">{c.year} · {fmt(c.mileage_km)} км · {c.engine_cc} см³</div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-white/40">
        <span className="rounded bg-white/5 px-1.5 py-0.5 uppercase tracking-wide">{c.source || "—"}</span>
        {c.url && (
          <a href={c.url} target="_blank" rel="noreferrer" data-testid="card-source-link"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-white/50 underline-offset-2 hover:text-white hover:underline">
            Оригінал ↗
          </a>
        )}
      </div>
      <div className="mt-3 flex items-end justify-between">
        <div>
          <div className="text-[11px] text-white/40">Під ключ в Україні</div>
          <div className="font-heading text-lg font-bold">{fmt(c.final_price_uah)} ₴</div>
        </div>
        <div className="text-right text-xs text-white/45">в ЄС<br />{fmt(c.listing_price)} €</div>
      </div>
      <p className="mt-2 line-clamp-2 text-xs text-white/60">{c.recommendation_uk}</p>
      <div className="mt-3 flex gap-2">
        <button data-testid="card-analyze" onClick={() => onAnalyze(c)}
          className="flex-1 rounded-lg border border-white/15 bg-white/5 py-2 text-sm font-medium transition-colors hover:border-[#C73D3A]">
          Аналіз
        </button>
        <button data-testid="card-order" onClick={() => onOrder(c)}
          className="flex-1 rounded-lg bg-[#C73D3A] py-2 text-sm font-semibold text-white transition-colors hover:bg-[#d84a47]">
          Довіз
        </button>
      </div>
    </div>
  </div>
);

const SUGGESTIONS = [
  "Чи надійний цей двигун?",
  "Автомат чи механіка — що краще?",
  "Які типові слабкі місця цієї моделі?",
];

function ChatPanel({ vehicle }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sid, setSid] = useState(null);
  const [busy, setBusy] = useState(false);

  async function send(q) {
    const question = (q ?? text).trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: "user", content: question }]);
    setText(""); setBusy(true);
    try {
      const r = await fetch(`${API}/chat/message`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: question, vehicle_context: vehicle }),
      });
      if (!r.ok) throw new Error("bad status");
      const d = await r.json();
      setSid(d.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: d.reply || "Спробуйте, будь ласка, ще раз за мить." }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Зараз я трохи зайнята — спробуйте, будь ласка, ще раз за кілька секунд." }]);
    }
    setBusy(false);
  }

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="chat-panel">
      <div className="mb-4 flex items-center gap-2">
        <MessageCircle size={18} className="text-[#C73D3A]" />
        <h2 className="font-heading text-lg font-semibold">Запитайте Софі</h2>
        <span className="text-xs text-white/40">— вона радить як фахівець з авто</span>
      </div>

      <div className="mb-4 max-h-[360px] space-y-3 overflow-y-auto" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/70 transition-colors hover:border-[#C73D3A] hover:text-white">
                {s}
              </button>
            ))}
          </div>
        )}
        {msgs.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-[#C73D3A] px-4 py-2.5 text-[15px] text-white">{m.content}</div>
            </div>
          ) : (
            <div key={i} className="flex gap-2.5">
              <SofiAvatar size={32} state="idle" ringWidth={1} nod className="shrink-0" testid="sofi-chat-avatar" />
              <div className="max-w-[80%] whitespace-pre-line rounded-2xl rounded-bl-sm border border-white/10 bg-white/[0.04] px-4 py-2.5 text-[15px] leading-relaxed text-white/85">{m.content}</div>
            </div>
          )
        )}
        {busy && (
          <div className="flex items-center gap-2 text-sm text-white/40" data-testid="chat-typing">
            <SofiAvatar size={26} state="thinking" ringWidth={1} className="shrink-0" testid="sofi-chat-thinking" />
            Софі друкує…
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <input data-testid="chat-input" className="inp flex-1" placeholder="Напишіть питання про авто…"
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} />
        <button data-testid="chat-send" onClick={() => send()} disabled={busy}
          className="flex items-center gap-2 rounded-xl bg-[#C73D3A] px-4 py-2.5 font-medium transition-colors hover:bg-[#d84a47] disabled:opacity-60">
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
